// src/pages/returnBatch/CreateReturnBatchPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Package,
  Scan,
  CheckCircle,
  AlertCircle,
  Loader2,
  Truck,
  MapPin,
  User,
  Phone,
  Hash,
  Clock,
  TrendingUp,
  X,
  BarChart3,
  ChevronDown,
  Store,
  CreditCard,
  ArrowLeft,
  Plus
} from 'lucide-react';
import {
  getTodayReturnBatches,
  createReturnBatch,
  addOrderToReturnBatch,
  closeReturnBatch,
  getUserWarehouses
} from '../../services/api/returnBatch';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import styles from './CreateReturnBatchPage.module.css';

const CreateReturnBatchPage = () => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success: showSuccess, error: showError } = useAlert();
  const inputRef = useRef(null);

  // Reference data
  const [warehouses, setWarehouses] = useState([]);
  const [businessCourierAccounts, setBusinessCourierAccounts] = useState([]);

  // Selection state
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  // Batch state
  const [todayBatches, setTodayBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [showBatchSelector, setShowBatchSelector] = useState(false);
  const [showNewBatchForm, setShowNewBatchForm] = useState(false);
  const [newBatchNotes, setNewBatchNotes] = useState('');

  // Scan state
  const [identifier, setIdentifier] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Recent returns (session only)
  const [recentReturns, setRecentReturns] = useState([]);
  const [sessionCount, setSessionCount] = useState(0);

  // Current return result
  const [currentReturn, setCurrentReturn] = useState(null);
  const [error, setError] = useState(null);

  // Statistics
  const [todayStats, setTodayStats] = useState({ totalOrders: 0, totalValue: 0 });

  // Audio feedback
  const successAudioRef = useRef(new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCl+zPHaizsIHGS57OihUQ0PVK3n77BdGAg+mdv0xnMoBSuAzvLZjToIGme78OScTgwOUqji8bllHAU2jdXzzn0vBSh7yvDekkAJElyx6OyrWBYKQ5zd8sFuJAQnfMrw3Y8+CRRas+jvo1QQDFWt5vCwYhoIOZXX8sh0KwUrgs7y2Ys5CBlluu7mnlEODlOq5PC0aB4GLIvP8MZ2LgUrf87w3I4+CRZftulwpFgTDEmh3+29bC'));
  const errorAudioRef = useRef(new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBgoOFh4mJiomJhIOAfXt5d3VycW9ubGppaGdmZWVkZGNjY2JiYmJiYmJiYmJiYmNjY2RkZWZnaGlrbG5wcnV3eXt9gIOFh4mKiomJh4WDgX97d3NwbGhlYV5aV1RSUE5MSklHRkRDQkFAQD8/Pz4+Pj4+Pj4+Pj4+Pz9AQEFCREVHSElLTU9RVFdaXmFlaG1wcXZ5fX+Dh4mLjY+QkZKTk5SUlJSUlJOTkpGQj42MioiGhIOBfnx5dnRxb2xpZmNgXVpXVVJQTkxKSEZFQ0JBQEBAQEBAQEBBQkNFRkhKTE5QUlVXWl1gY2ZpbHF0d3p8f4GDhoiKjI6QkpOUlZWWlpaWlpWVlJOSkZCOjYuJh4WDgX99enZ0cW9saWZjYF1aV1RSUE5NSUtJSEdGRURDQ0JCQkJCQkJCQ0NFRkhKTE5RVFdaXWFkaGtwdHd7foGFiIuNj5KUlZaYmJmZmZmZmJiXlpWUkpCOjIqHhYOAfl1ZVlNQTUqF'));

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Load batches when courier is selected
  useEffect(() => {
    if (selectedCourierId && selectedWarehouseId) {
      loadTodayBatches();
    } else {
      setTodayBatches([]);
      setActiveBatch(null);
    }
  }, [selectedCourierId, selectedWarehouseId]);

  // Auto-focus input when ready
  useEffect(() => {
    if (activeBatch && inputRef.current && !isProcessing) {
      inputRef.current.focus();
    }
  }, [activeBatch, isProcessing, currentReturn, error]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F2 to focus input
      if (e.key === 'F2') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      // ESC to clear
      if (e.key === 'Escape') {
        handleClear();
      }
      // Ctrl+N for new batch
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        if (selectedCourierId && selectedWarehouseId) {
          setShowNewBatchForm(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCourierId, selectedWarehouseId]);

  const loadReferenceData = async () => {
    try {
      const [warehousesRes, couriersRes] = await Promise.all([
        getUserWarehouses(),
        getBusinessCouriers()
      ]);

      const warehouseList = warehousesRes.warehouses || [];
      const courierList = couriersRes.couriers || [];

      setWarehouses(warehouseList);
      setBusinessCourierAccounts(courierList);

      // Auto-select if only one option
      if (warehouseList.length === 1) {
        setSelectedWarehouseId(warehouseList[0].id.toString());
      }
      if (courierList.length === 1) {
        setSelectedCourierId(courierList[0].id.toString());
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
      showError(t('returnBatch.failedToLoadReferenceData'));
    }
  };

  const loadTodayBatches = async () => {
    try {
      const response = await getTodayReturnBatches(parseInt(selectedCourierId));
      const batches = response.batches || [];
      setTodayBatches(batches);

      if (batches.length === 0) {
        setActiveBatch(null);
        setShowBatchSelector(false);
      } else if (batches.length === 1) {
        setActiveBatch(batches[0]);
        setShowBatchSelector(false);
        updateTodayStats(batches[0]);
      } else {
        setShowBatchSelector(true);
        setActiveBatch(batches[0]); // Auto-select first
        updateTodayStats(batches[0]);
      }
    } catch (error) {
      console.error('Failed to load today batches:', error);
      showError(t('returnBatch.failedToLoadBatches'));
    }
  };

  const updateTodayStats = (batch) => {
    if (batch) {
      setTodayStats({
        totalOrders: batch.totalOrders || 0,
        totalValue: batch.totalValue || 0
      });
    }
  };

  const handleCreateNewBatch = async () => {
    if (!selectedCourierId) {
      showError(t('returnBatch.pleaseSelectCourier'));
      return;
    }
    if (!selectedWarehouseId) {
      showError(t('returnBatch.pleaseSelectWarehouse'));
      return;
    }

    try {
      const response = await createReturnBatch({
        businessCourierId: parseInt(selectedCourierId),
        warehouseId: parseInt(selectedWarehouseId),
        notes: newBatchNotes
      });

      setActiveBatch({
        id: response.id,
        batchNumber: response.batchNumber,
        totalOrders: 0,
        totalValue: 0,
        courierAccount: response.courierAccount,
        warehouse: warehouses.find(w => w.id === parseInt(selectedWarehouseId)),
        createdAt: new Date().toISOString()
      });

      setShowNewBatchForm(false);
      setNewBatchNotes('');
      showSuccess(t('returnBatch.batchCreatedSuccessfully').replace('{batchNumber}', response.batchNumber));

      // Reload batches
      await loadTodayBatches();
    } catch (error) {
      console.error('Failed to create batch:', error);
      showError(error.response?.data?.message || t('returnBatch.failedToCreateBatch'));
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();

    if (!identifier.trim()) {
      setError(t('returnBatch.pleaseScanOrder'));
      return;
    }

    // Auto-create batch if needed
    if (!activeBatch) {
      try {
        const response = await createReturnBatch({
          businessCourierId: parseInt(selectedCourierId),
          warehouseId: parseInt(selectedWarehouseId),
          notes: ''
        });

        const newBatch = {
          id: response.id,
          batchNumber: response.batchNumber,
          totalOrders: 0,
          totalValue: 0,
          courierAccount: response.courierAccount,
          warehouse: warehouses.find(w => w.id === parseInt(selectedWarehouseId)),
          createdAt: new Date().toISOString()
        };

        setActiveBatch(newBatch);
        showSuccess(t('returnBatch.batchCreatedAutomatically').replace('{batchNumber}', response.batchNumber));

        // Proceed with scan
        await processReturn(response.id);
      } catch (error) {
        console.error('Failed to create batch:', error);
        showError(error.response?.data?.message || t('returnBatch.failedToCreateBatch'));
        return;
      }
    } else {
      await processReturn(activeBatch.id);
    }
  };

  const processReturn = async (batchId) => {
    setIsProcessing(true);
    setError(null);
    setCurrentReturn(null);

    try {
      const result = await addOrderToReturnBatch(batchId, {
        identifier: identifier.trim(),
        notes: ''
      });

      // Play success sound
      successAudioRef.current?.play().catch(() => {});

      // Update current return
      setCurrentReturn(result);

      // Add to recent returns
      const returnRecord = {
        ...result.order,
        stockMovements: result.stockMovements,
        returnedAt: new Date().toISOString()
      };
      setRecentReturns(prev => [returnRecord, ...prev.slice(0, 9)]);

      // Update counters
      setSessionCount(prev => prev + 1);
      setActiveBatch(prev => ({
        ...prev,
        totalOrders: result.returnBatch.totalOrders,
        totalValue: result.returnBatch.totalValue
      }));
      setTodayStats({
        totalOrders: result.returnBatch.totalOrders,
        totalValue: result.returnBatch.totalValue
      });

      // Clear input
      setIdentifier('');

      // Show success message
      showSuccess(t('returnBatch.orderReturnedSuccessfully').replace('{orderNumber}', result.order.orderNumber));
    } catch (error) {
      console.error('Return error:', error);

      // Play error sound
      errorAudioRef.current?.play().catch(() => {});

      // Set error message
      const errorMessage = error.response?.data?.message || error.message || t('returnBatch.failedToReturn');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    setIdentifier('');
    setError(null);
    setCurrentReturn(null);
    inputRef.current?.focus();
  };

  const handleCloseBatch = async () => {
    if (!activeBatch) return;

    const confirmed = window.confirm(
      t('returnBatch.confirmCloseBatch').replace('{batchNumber}', activeBatch.batchNumber)
    );

    if (!confirmed) return;

    try {
      await closeReturnBatch(activeBatch.id);
      showSuccess(t('returnBatch.batchClosedSuccessfully'));
      setActiveBatch(null);
      setRecentReturns([]);
      setSessionCount(0);
      await loadTodayBatches();
    } catch (error) {
      console.error('Failed to close batch:', error);
      showError(error.response?.data?.message || t('returnBatch.failedToCloseBatch'));
    }
  };

  const selectedCourier = businessCourierAccounts.find(
    c => c.id.toString() === selectedCourierId
  );

  const selectedWarehouse = warehouses.find(
    w => w.id.toString() === selectedWarehouseId
  );

  return (
    <div className={styles.returnBatchPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Package className={styles.headerIcon} size={28} />
            <div>
              <h1>{t('returnBatch.createReturnBatch')}</h1>
              <p>{t('returnBatch.scanToRestoreInventory')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#dbeafe' }}>
            <Package size={24} style={{ color: '#2563eb' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>{t('returnBatch.batchReturns')}</div>
            <div className={styles.statValue}>{todayStats.totalOrders}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#dcfce7' }}>
            <TrendingUp size={24} style={{ color: '#16a34a' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>{t('returnBatch.batchValue')}</div>
            <div className={styles.statValue}>{formatCurrency(todayStats.totalValue)}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#fef3c7' }}>
            <BarChart3 size={24} style={{ color: '#d97706' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>{t('returnBatch.thisSession')}</div>
            <div className={styles.statValue}>{sessionCount}</div>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        {/* Scan Section */}
        <div className={styles.scanSection}>
          {/* Courier & Warehouse Selection */}
          <div className={styles.selectionCard}>
            <h2>
              <Truck size={20} />
              {t('returnBatch.setup')}
            </h2>
            <div className={styles.selectionGrid}>
              <div className={styles.formGroup}>
                <label>{t('returnBatch.courierAccount')} *</label>
                <div className={styles.selectWrapper}>
                  <Truck className={styles.inputIcon} size={18} />
                  <select
                    value={selectedCourierId}
                    onChange={(e) => setSelectedCourierId(e.target.value)}
                    className={styles.select}
                  >
                    <option value="">{t('returnBatch.selectCourier')}</option>
                    {businessCourierAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.accountName} ({account.CourierCompany?.name})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className={styles.selectArrow} size={18} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>{t('returnBatch.warehouse')} *</label>
                <div className={styles.selectWrapper}>
                  <MapPin className={styles.inputIcon} size={18} />
                  <select
                    value={selectedWarehouseId}
                    onChange={(e) => setSelectedWarehouseId(e.target.value)}
                    className={styles.select}
                  >
                    <option value="">{t('returnBatch.selectWarehouse')}</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                  <ChevronDown className={styles.selectArrow} size={18} />
                </div>
              </div>
            </div>
          </div>

          {/* Batch Selection/Info */}
          {selectedCourierId && selectedWarehouseId && (
            <>
              {showBatchSelector && todayBatches.length > 1 && (
                <div className={styles.batchSelectorCard}>
                  <h3>{t('returnBatch.multipleActiveBatchesFound')}</h3>
                  <div className={styles.batchList}>
                    {todayBatches.map(batch => (
                      <div
                        key={batch.id}
                        className={`${styles.batchOption} ${
                          activeBatch?.id === batch.id ? styles.selected : ''
                        }`}
                        onClick={() => {
                          setActiveBatch(batch);
                          updateTodayStats(batch);
                        }}
                      >
                        <div className={styles.batchInfo}>
                          <strong>{batch.batchNumber}</strong>
                          <span>{batch.totalOrders} {t('returnBatch.orders').toLowerCase()} · {formatCurrency(batch.totalValue)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!activeBatch && !showNewBatchForm && todayBatches.length === 0 && (
                <div className={styles.noBatchCard}>
                  <AlertCircle size={32} />
                  <h3>{t('returnBatch.noActiveBatch')}</h3>
                  <p>{t('returnBatch.newBatchWillBeCreated')}</p>
                  <button
                    onClick={() => setShowNewBatchForm(true)}
                    className={styles.createBatchButton}
                  >
                    <Plus size={18} />
                    {t('returnBatch.createBatchNow')}
                  </button>
                </div>
              )}

              {showNewBatchForm && (
                <div className={styles.newBatchForm}>
                  <h3>{t('returnBatch.createNewBatch')}</h3>
                  <div className={styles.formGroup}>
                    <label>{t('returnBatch.notesOptional')}</label>
                    <textarea
                      value={newBatchNotes}
                      onChange={(e) => setNewBatchNotes(e.target.value)}
                      placeholder={t('returnBatch.notesPlaceholder')}
                      className={styles.textarea}
                      rows={3}
                    />
                  </div>
                  <div className={styles.formActions}>
                    <button
                      onClick={() => setShowNewBatchForm(false)}
                      className={styles.cancelButton}
                    >
                      {t('returnBatch.cancel')}
                    </button>
                    <button
                      onClick={handleCreateNewBatch}
                      className={styles.submitButton}
                    >
                      {t('returnBatch.createBatch')}
                    </button>
                  </div>
                </div>
              )}

              {activeBatch && (
                <div className={styles.activeBatchCard}>
                  <div className={styles.batchHeader}>
                    <h2>
                      <CheckCircle size={20} style={{ color: '#16a34a' }} />
                      {t('returnBatch.activeBatch')}
                    </h2>
                    <div className={styles.batchActions}>
                      {todayBatches.length > 1 && (
                        <button
                          onClick={() => setShowBatchSelector(!showBatchSelector)}
                          className={styles.switchButton}
                        >
                          {t('returnBatch.switchBatch')}
                        </button>
                      )}
                      <button
                        onClick={() => setShowNewBatchForm(true)}
                        className={styles.newBatchButton}
                      >
                        <Plus size={16} />
                        {t('returnBatch.newBatch')}
                      </button>
                      <button
                        onClick={handleCloseBatch}
                        className={styles.closeButton}
                      >
                        {t('returnBatch.closeBatch')}
                      </button>
                    </div>
                  </div>
                  <div className={styles.batchDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.batchNumber')}:</span>
                      <strong>{activeBatch.batchNumber}</strong>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.courier')}:</span>
                      <span>{selectedCourier?.accountName}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.warehouse')}:</span>
                      <span>{selectedWarehouse?.name}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.totalOrders')}:</span>
                      <strong className={styles.successText}>{activeBatch.totalOrders}</strong>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.totalValue')}:</span>
                      <strong className={styles.successText}>
                        {formatCurrency(activeBatch.totalValue)}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Scan Interface */}
          {activeBatch && (
            <div className={styles.scanCard}>
              <div className={styles.scanHeader}>
                <Scan size={32} />
                <h2>{t('returnBatch.scanToReturn')}</h2>
              </div>

              <form onSubmit={handleScan} className={styles.scanForm}>
                <div className={styles.inputGroup}>
                  <label>{t('returnBatch.orderNumber')} *</label>
                  <div className={styles.scanInputWrapper}>
                    <Hash className={styles.inputIcon} size={20} />
                    <input
                      ref={inputRef}
                      type="text"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder={t('returnBatch.scanPlaceholder')}
                      className={styles.scanInput}
                      disabled={isProcessing}
                      autoComplete="off"
                    />
                    {identifier && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className={styles.clearButton}
                        disabled={isProcessing}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.actionButtons}>
                  <button
                    type="submit"
                    disabled={isProcessing || !identifier.trim()}
                    className={styles.returnButton}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 size={20} className={styles.spinning} />
                        {t('returnBatch.processing')}
                      </>
                    ) : (
                      <>
                        <CheckCircle size={20} />
                        {t('returnBatch.processReturns')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={isProcessing}
                    className={styles.clearButtonLarge}
                  >
                    <X size={20} />
                    {t('returnBatch.clear')}
                  </button>
                </div>
              </form>

              {/* Error Display */}
              {error && (
                <div className={styles.errorAlert}>
                  <AlertCircle size={20} />
                  <div className={styles.errorContent}>
                    <strong>{t('returnBatch.returnFailed')}</strong>
                    <p>{error}</p>
                  </div>
                </div>
              )}

              {/* Success Display */}
              {currentReturn && (
                <div className={styles.successAlert}>
                  <div className={styles.successHeader}>
                    <CheckCircle size={24} />
                    <h3>{t('returnBatch.orderReturnedSuccessfully')}</h3>
                  </div>

                  <div className={styles.returnDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.orderNumber')}:</span>
                      <span className={styles.detailValue}>#{currentReturn.order.orderNumber}</span>
                    </div>

                    {currentReturn.order.airwaybillNumber && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('returnBatch.airwaybill')}:</span>
                        <span className={styles.detailValue}>{currentReturn.order.airwaybillNumber}</span>
                      </div>
                    )}

                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.customer')}:</span>
                      <span className={styles.detailValue}>{currentReturn.order.customerName}</span>
                    </div>

                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.status')}:</span>
                      <span className={styles.statusBadge}>{currentReturn.order.newStatus}</span>
                    </div>

                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('returnBatch.grandTotal')}:</span>
                      <span className={styles.amountValue}>
                        {formatCurrency(currentReturn.order.grandTotal)}
                      </span>
                    </div>

                    {currentReturn.order.codAmount > 0 && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{t('returnBatch.codAmount')}:</span>
                        <span className={styles.codValue}>
                          {formatCurrency(currentReturn.order.codAmount)}
                        </span>
                      </div>
                    )}

                    {/* Stock Movements */}
                    {currentReturn.stockMovements && currentReturn.stockMovements.length > 0 && (
                      <div className={styles.stockSection}>
                        <h4>{t('returnBatch.stockRestored')}:</h4>
                        {currentReturn.stockMovements.map((movement, idx) => (
                          <div key={idx} className={styles.stockItem}>
                            <span className={styles.productName}>{movement.productName}</span>
                            {movement.variationSku && (
                              <span className={styles.sku}>({movement.variationSku})</span>
                            )}
                            <span className={styles.quantity}>{t('returnBatch.qty')}: {movement.quantityRestored}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Keyboard Shortcuts */}
          <div className={styles.shortcutsCard}>
            <h3>{t('returnBatch.keyboardShortcuts')}</h3>
            <div className={styles.shortcutsList}>
              <div className={styles.shortcutItem}>
                <kbd>F2</kbd>
                <span>{t('returnBatch.focusInput')}</span>
              </div>
              <div className={styles.shortcutItem}>
                <kbd>Enter</kbd>
                <span>{t('returnBatch.submitScan')}</span>
              </div>
              <div className={styles.shortcutItem}>
                <kbd>ESC</kbd>
                <span>{t('returnBatch.clearForm')}</span>
              </div>
              <div className={styles.shortcutItem}>
                <kbd>Ctrl+N</kbd>
                <span>{t('returnBatch.newBatch')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Returns */}
        <div className={styles.recentSection}>
          <div className={styles.recentCard}>
            <div className={styles.recentHeader}>
              <h2>
                <Clock size={20} />
                {t('returnBatch.recentReturns')}
              </h2>
              <span className={styles.recentCount}>{recentReturns.length}</span>
            </div>

            {recentReturns.length > 0 ? (
              <div className={styles.recentList}>
                {recentReturns.map((returnItem, index) => (
                  <div key={index} className={styles.recentItem}>
                    <div className={styles.recentIcon}>
                      <CheckCircle size={20} style={{ color: '#16a34a' }} />
                    </div>
                    <div className={styles.recentContent}>
                      <div className={styles.recentOrder}>
                        <strong>#{returnItem.orderNumber}</strong>
                        {returnItem.airwaybillNumber && (
                          <span className={styles.recentAwb}>{returnItem.airwaybillNumber}</span>
                        )}
                      </div>
                      <div className={styles.recentCustomer}>
                        <User size={12} />
                        {returnItem.customerName}
                      </div>
                      {returnItem.shop && (
                        <div className={styles.recentShop}>
                          <Store size={12} />
                          {returnItem.shop}
                        </div>
                      )}
                      <div className={styles.recentAmount}>
                        {formatCurrency(returnItem.grandTotal)}
                      </div>
                    </div>
                    <div className={styles.recentTime}>
                      {new Date(returnItem.returnedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyRecent}>
                <Package size={48} />
                <p>{t('returnBatch.noReturnsYet')}</p>
                <span>{t('returnBatch.returnedOrders')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReturnBatchPage;